import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';
import { type Ref, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';
import type { ObligationTableFields } from 'src/pages/compliance/obligations/types';

import { useGetWidgetData } from '../../gigawidget/hooks/useGetWidgetData';
import type { WidgetRef } from '../../types';
import { dataSources } from '../../universal-widget/data-sources';
import type { FilterSettings } from '../../universal-widget/util';
import { useWidgetSettingsHandler } from '../../widget-context/useWidgetSettingsHandler';
import LineChartWidget from '../line-chart-widget/LineChartWidget';
import { WidgetSettingsModal } from '../widget-settings-modal/WidgetSettingsModal';
import { resultHistoryWindowFilter } from './resultHistoryWindow';
import { sortByDateX } from './utils';

export const ComplianceRatingsOverTime = (
  _props: unknown,
  ref: Ref<WidgetRef>
) => {
  const [showModal, setShowModal] = useState(false);
  const [settings, setSettings] = useDashboardWidgetSettings<FilterSettings>();
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.widgets.complianceRatingsOverTime',
  });

  useWidgetSettingsHandler(ref, () => setShowModal(true));

  const dataSource = dataSources.obligation;
  const {
    tableProps: { allItems },
    loading,
  } = useGetWidgetData({
    dataSource,
    propertyFilterQuery: settings?.filtering,
    // COI-837 — restores the nested clip this widget lost in COI-600, now on
    // BOTH legs. `resultHistoryWindow` clips the nested assessment-result
    // series; it deliberately does not narrow the obligation rows, matching the
    // `obligationAssessmentResultsWhere` this replaces.
    seriesDateFilterOptions: { dateFilter: resultHistoryWindowFilter },
  });

  const series = useObligationRatingsOverTime(allItems);

  const { getByValue: getPerformanceByValue } = useRating('performance_result');

  const handleSave = async (data: FilterSettings) => {
    setSettings(data);
  };

  return (
    <>
      {showModal && (
        <WidgetSettingsModal
          onDismiss={() => setShowModal(false)}
          onSave={handleSave}
          dataSource={dataSource}
        />
      )}
      <LineChartWidget
        series={series}
        isLoading={loading}
        xTitle={t('xTitle')}
        yTitle={t('yTitle')}
        tooltipYValueFormatter={(y) =>
          getPerformanceByValue(y)?.label || String(y)
        }
        pointColorFormatter={(y) =>
          getColorStyles(getPerformanceByValue(y)?.color)?.backgroundColor
        }
      />
    </>
  );
};

const useObligationRatingsOverTime = (
  data: readonly ObligationTableFields[] | undefined
) => {
  const series = data
    ? data.flatMap((obligation) => {
        if (
          !obligation.assessmentResults ||
          obligation.assessmentResults.length === 0
        ) {
          return [];
        }

        return {
          name: obligation.Title,
          data: obligation.assessmentResults
            .filter((result) => Boolean(result.obligationAssessmentResult))
            .map((result) => ({
              x:
                result.obligationAssessmentResult!.TestDate ??
                result.obligationAssessmentResult!.CreatedAtTimestamp,
              y: result.obligationAssessmentResult!.Rating ?? 0,
            }))
            .sort(sortByDateX),
        };
      })
    : [];

  return series;
};
